/**
 * Pure logic for inventory check/warning: task-level (operation source) and plan-level (global or operation).
 * Used by packingTaskGenerator (operations), TripDetailScreen (global trip), and QuantitySummary display.
 * Fully unit-testable without DB or React.
 */

export type TaskWarning =
    | { type: 'unavailable'; message: string }
    | { type: 'insufficient'; available: number; required: number; shortage: number; message: string };

/**
 * Compute task-level warnings from available quantity at source vs required.
 * Used for operations: per-task source availability (current inventory at that source).
 */
export function computeTaskWarnings(availableAtSource: number, quantityRequired: number, locationName: string): TaskWarning[] {
    if (availableAtSource === 0) {
        return [{ type: 'unavailable', message: `No items available at ${locationName}` }];
    }
    if (quantityRequired > availableAtSource) {
        const shortage = quantityRequired - availableAtSource;
        return [
            {
                type: 'insufficient',
                available: availableAtSource,
                required: quantityRequired,
                shortage,
                message: `Only ${availableAtSource} available, need ${quantityRequired} (shortage: ${shortage}) at ${locationName}`,
            },
        ];
    }
    return [];
}

export type ItemLocationDataLike = { itemTypeId: string; quantity: number };

/**
 * Get available quantity at a single source location for an item type.
 * Pure lookup: used for operations (current inventory at source key).
 */
export function getAvailableAtSource(
    itemTypeId: string,
    sourceLocationKey: string,
    itemLocationsByLocationKey: Map<string, ItemLocationDataLike[]>
): number {
    const locs = itemLocationsByLocationKey.get(sourceLocationKey) ?? [];
    const loc = locs.find(l => l.itemTypeId === itemTypeId);
    return loc?.quantity ?? 0;
}

/**
 * Availability status for plan-level checks (global or operation-scoped).
 * Uses lowThreshold: sufficient if available >= required; low if available >= required * threshold; else insufficient.
 */
export type AvailabilityStatus = 'sufficient' | 'low' | 'insufficient';

export function availabilityStatus(required: number, available: number, lowThreshold: number = 0.8): AvailabilityStatus {
    if (available >= required) return 'sufficient';
    if (required <= 0) return 'sufficient';
    if (available >= required * lowThreshold) return 'low';
    return 'insufficient';
}

export interface InsufficientItem {
    itemTypeId: string;
    shortage: number;
    required: number;
}

/**
 * Plan-level: which item types have insufficient availability vs their total needed.
 * getAvailable(itemTypeId) can be global (all locations) or operation-scoped (selected sources).
 */
export function computeInsufficientItemsFromPlans(
    quantityPlans: Map<string, { totalNeeded: number }>,
    getAvailable: (itemTypeId: string) => number,
    options?: { lowThreshold?: number }
): InsufficientItem[] {
    const lowThreshold = options?.lowThreshold ?? 0.8;
    const insufficient: InsufficientItem[] = [];
    for (const [itemTypeId, plan] of Array.from(quantityPlans.entries())) {
        const required = plan.totalNeeded;
        const available = getAvailable(itemTypeId);
        const status = availabilityStatus(required, available, lowThreshold);
        if (status === 'insufficient') {
            insufficient.push({
                itemTypeId,
                shortage: Math.max(0, required - available),
                required,
            });
        }
    }
    return insufficient;
}

/**
 * Display status for icons/colors: success (have >= need), warning (have > 0), error (have === 0).
 * Used by QuantitySummary and similar UI.
 */
export type DisplayStatus = 'success' | 'warning' | 'error';

export function displayStatus(need: number, have: number): DisplayStatus {
    if (have >= need) return 'success';
    if (have > 0) return 'warning';
    return 'error';
}
